import React from 'react';
import { crnaNameplateColor, doctorBullpenColor, studentColorFor } from './staffNameplates.ts';
import { earliestShiftByEmployee, providerCardShift } from './boardViewHelpers.ts';
import { utcToLocalTime } from '../utils/timezone.ts';
import { FitText } from './RoomNamePlates.tsx';
import EmployeeDetailsPopover from './EmployeeDetailsPopover.tsx';
import type { DoctorSupervision } from './EmployeeDetailsPopover.tsx';
import type { Employee } from '../services/api.ts';

// The provider-card prop bundle BoardView builds for a bullpen person — the SAME card the board nameplates
// render. Every field mirrors an EmployeeDetailsPopover prop and every one is optional: the room-relative
// ones are absent for someone not yet placed, and `employee` is absent when the builder cannot resolve the id
// (the brick then falls back to its own record rather than opening an empty card).
type BullpenCardProps = Partial<Omit<React.ComponentProps<typeof EmployeeDetailsPopover>, 'children' | 'triggerStyle'>>;

// The bullpen: available providers for the day as draggable "bricks", in three columns
// (Anesthesiologists | CRNAs | Students). Non-pecking-order staff are grouped and labeled by
// their shift WINDOW (e.g. "0630-1500"); pecking-order doctors get a numbered group; students
// group by competency. Static — it does NOT change with the timeline bar. Staffing a room removes
// a provider from the bullpen (excludeIds); freeing a room returns them. Click a brick for the card.

interface EmployeeLike { employeeId: string; firstName: string; lastName: string; employeeType: string; studentCompetency?: string; homeHospitalId?: string; }
interface ShiftLike { employeeId: string; startTimeUtc?: string; endTimeUtc?: string; order?: number | null }
interface HospitalLike { hospitalId: string; name: string }
// One room that still needs a provider (doctor or CRNA), for the bullpen NEED nameplate.
export interface StaffNeed { roomLabel: string; hospital: string; times: string[] }

const localHM = (utc?: string): string => (utc ? utcToLocalTime(utc) : '');            // "05:30"
const hhmm = (utc?: string): string => localHM(utc).replace(':', '');                   // "0530"
const localMin = (utc?: string): number => { const t = localHM(utc); return t ? (+t.slice(0, 2)) * 60 + (+t.slice(3, 5)) : 99999; };

const Bullpen: React.FC<{ employees: EmployeeLike[]; shifts: ShiftLike[]; hospitals?: HospitalLike[]; excludeIds?: Set<string>; dimIds?: Set<string>; supervisionFor?: (id: string) => DoctorSupervision; onRemoveStaff?: (fromScheduleId: string, employeeId: string) => void; colorOverrideFor?: (employeeId: string) => string | undefined; orderOverride?: Record<string, number>; doctorNeeds?: StaffNeed[]; crnaNeeds?: StaffNeed[]; cardFor?: (employeeId: string) => BullpenCardProps }> = ({ employees, shifts, hospitals = [], excludeIds, dimIds, supervisionFor, onRemoveStaff, colorOverrideFor, orderOverride, doctorNeeds = [], crnaNeeds = [], cardFor }) => {
  const [dropActive, setDropActive] = React.useState(false);
  // Which NEED nameplate's room list is expanded (click a NEED brick to see the rooms/times it covers).
  const [openNeed, setOpenNeed] = React.useState<null | 'doctor' | 'crna'>(null);
  React.useEffect(() => {
    if (!openNeed) return;
    const close = () => setOpenNeed(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openNeed]);
  const hospitalNameById = new Map(hospitals.map(h => [h.hospitalId, h.name]));

  // Earliest shift per employee (drives grouping, ordering, and shift color) — the same shared pick the
  // provider card resolves, so a brick and the card it opens always describe the same shift.
  const shiftByEmp = earliestShiftByEmployee(shifts);
  const availableIds = new Set(shifts.map(s => s.employeeId));
  const available = employees.filter(e => availableIds.has(e.employeeId) && !(excludeIds?.has(e.employeeId)));

  // Pecking-order number: a manual reorder from the Anesthesiologists tab (orderOverride) wins over the
  // shift's stored order, so dragging docs there re-numbers them here too.
  const orderOf = (id: string): number | null => {
    if (orderOverride && orderOverride[id] !== undefined) return orderOverride[id];
    const o = shiftByEmp.get(id)?.order;
    return o === undefined || o === null ? null : o;
  };
  const windowLabel = (id: string): string => {
    const s = shiftByEmp.get(id);
    return s && s.startTimeUtc && s.endTimeUtc ? `${hhmm(s.startTimeUtc)}-${hhmm(s.endTimeUtc)}` : 'No shift';
  };
  const byLastName = (a: EmployeeLike, b: EmployeeLike) => a.lastName.localeCompare(b.lastName);

  // Group a list of workers by their shift-window label, ordered by start then end time.
  const groupByWindow = (list: EmployeeLike[]) => {
    const groups = new Map<string, { label: string; startMin: number; endMin: number; items: EmployeeLike[] }>();
    for (const e of list) {
      const lbl = windowLabel(e.employeeId);
      const s = shiftByEmp.get(e.employeeId);
      if (!groups.has(lbl)) groups.set(lbl, { label: lbl, startMin: localMin(s?.startTimeUtc), endMin: localMin(s?.endTimeUtc), items: [] });
      groups.get(lbl)!.items.push(e);
    }
    // Sort groups by END time first (so a 1900 shift sorts after 1700 regardless of a 0530/0630 start),
    // then by start time as a tiebreaker.
    return [...groups.values()].sort((a, b) => a.endMin - b.endMin || a.startMin - b.startMin);
  };

  const doctors = available.filter(e => e.employeeType === 'doctor');
  const crnas = available.filter(e => e.employeeType === 'nurse');
  const students = available.filter(e => e.employeeType.startsWith('nurse-student'));

  const shiftDocGroups = groupByWindow(doctors.filter(e => orderOf(e.employeeId) === null));
  const orderedDocs = doctors.filter(e => orderOf(e.employeeId) !== null).sort((a, b) => orderOf(a.employeeId)! - orderOf(b.employeeId)!);
  // CRNAs mirror the doctors: pecking-order CRNAs (order set) get a numbered "Pecking order" group; the rest
  // are grouped by shift window. So a pecking-order CRNA shows its #N on its bullpen brick, like the doctor tab.
  const shiftCrnaGroups = groupByWindow(crnas.filter(e => orderOf(e.employeeId) === null));
  const orderedCrnas = crnas.filter(e => orderOf(e.employeeId) !== null).sort((a, b) => orderOf(a.employeeId)! - orderOf(b.employeeId)!);

  const brick = (e: EmployeeLike, color: string, number?: number) => {
    const shift = shiftByEmp.get(e.employeeId);
    // B1: a person marked "Off" (sent home) STAYS on the bullpen, greyed out, rather than vanishing — so the card
    // that opened doesn't unmount and the runner can still see (and un-off) them. Dimmed bricks aren't draggable
    // onto a room (they're home), but stay clickable for the card.
    const dimmed = dimIds?.has(e.employeeId) ?? false;
    const inner = (
      <div
        draggable={!dimmed}
        onDragStart={dimmed ? undefined : (ev) => {
          ev.dataTransfer.setData('application/x-staff', JSON.stringify({ employeeId: e.employeeId, employeeType: e.employeeType }));
          ev.dataTransfer.effectAllowed = 'copy';
        }}
        title={dimmed ? `${e.firstName} ${e.lastName} — sent home` : undefined}
        style={{
          background: color, color: '#fff', fontWeight: 600, borderRadius: '5px',
          padding: '0 6px', height: '22px', display: 'flex', alignItems: 'center', gap: '5px',
          cursor: dimmed ? 'pointer' : 'grab', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          opacity: dimmed ? 0.45 : 1, filter: dimmed ? 'grayscale(0.7)' : undefined,
        }}
      >
        {number !== undefined && <span style={{ fontSize: '0.72em', opacity: 0.85, fontWeight: 700, flexShrink: 0 }}>#{number}</span>}
        <span style={{ flex: 1, minWidth: 0 }}><FitText text={e.lastName} /></span>
        {dimmed && <span aria-hidden title="Sent home" style={{ fontSize: '0.72em', flexShrink: 0 }}>🏠</span>}
      </div>
    );
    // Prefer the full board-style card BoardView builds (identical to the room nameplates). If no builder was
    // supplied — or it could not resolve this id to an employee — fall back to the lightweight props built
    // from the brick's own record, so clicking a brick always opens a card.
    const rich = cardFor?.(e.employeeId);
    const card: BullpenCardProps = rich?.employee ? rich : {
      shift: providerCardShift(shift, orderOf(e.employeeId) ?? undefined),
      hospitalName: e.homeHospitalId ? hospitalNameById.get(e.homeHospitalId) : undefined,
      supervision: e.employeeType === 'doctor' && supervisionFor ? supervisionFor(e.employeeId) : undefined,
    };
    return (
      <EmployeeDetailsPopover
        key={e.employeeId}
        {...card}
        employee={rich?.employee ?? (e as unknown as Employee)}
        triggerStyle={{ display: 'block', width: '100%', textDecoration: 'none' }}
      >
        {inner}
      </EmployeeDetailsPopover>
    );
  };

  const subgroup = (label: string, items: EmployeeLike[], colorFn: (e: EmployeeLike) => string, numbered = false) =>
    items.length === 0 ? null : (
      <div key={label} style={{ marginBottom: '8px' }}>
        <div style={{ fontSize: '0.62em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', color: 'var(--text-faint)', marginBottom: '3px' }}>{label}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {items.map(e => brick(e, colorFn(e), numbered ? orderOf(e.employeeId)! : undefined))}
        </div>
      </div>
    );

  // Each column scrolls independently (its own vertical scrollbar) so a long list doesn't move the others.
  const column = (title: string, count: number, children: React.ReactNode) => (
    <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontSize: '0.7em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--text-muted)', marginBottom: '6px' }}>{title} ({count})</div>
      <div style={{ maxHeight: '72vh', overflowY: 'auto', overflowX: 'hidden', paddingRight: '4px' }}>{children}</div>
    </div>
  );

  // A red "NEED MD" / "NEED CRNA" nameplate at the top of a column whenever rooms are still short that
  // provider. Click it to expand the list of rooms + the case times where that provider is needed.
  const needNameplate = (kind: 'doctor' | 'crna', needs: StaffNeed[]) => {
    if (!needs.length) return null;
    const label = kind === 'doctor' ? 'NEED MD' : 'NEED CRNA';
    const open = openNeed === kind;
    return (
      <div style={{ position: 'relative', marginBottom: '8px' }} onMouseDown={(e) => e.stopPropagation()}>
        <button
          onClick={() => setOpenNeed(open ? null : kind)}
          title={`${needs.length} room${needs.length === 1 ? '' : 's'} still need ${kind === 'doctor' ? 'an anesthesiologist' : 'a CRNA'} — click for the list`}
          style={{ width: '100%', background: 'rgba(220,38,38,0.14)', border: '1px solid #dc2626', color: '#dc2626', fontWeight: 700, borderRadius: '5px', padding: '0 6px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontSize: '0.78em', letterSpacing: '0.3px' }}
        >
          <span>{label}</span>
          <span style={{ fontSize: '0.85em', background: '#dc2626', color: '#fff', borderRadius: '999px', minWidth: '16px', textAlign: 'center', padding: '0 5px' }}>{needs.length}</span>
        </button>
        {open && (
          <div style={{ position: 'absolute', top: '25px', left: 0, right: 0, zIndex: 30, background: 'var(--popover-bg)', border: '1px solid var(--popover-border)', borderRadius: '8px', boxShadow: 'var(--shadow-lg)', padding: '6px', maxHeight: '52vh', overflowY: 'auto' }}>
            <div style={{ fontSize: '0.62em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', color: 'var(--text-faint)', marginBottom: '4px' }}>
              {label} — {needs.length} room{needs.length === 1 ? '' : 's'}
            </div>
            {needs.map((n, i) => (
              <div key={i} style={{ padding: '4px 6px', borderRadius: '5px', fontSize: '0.8em', color: 'var(--text-primary)', borderBottom: i < needs.length - 1 ? '1px solid var(--border-soft)' : 'none' }}>
                <div style={{ fontWeight: 600 }}>{n.roomLabel} <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>· {n.hospital}</span></div>
                {n.times.length > 0 && <div style={{ color: 'var(--amber)', fontWeight: 700, fontSize: '0.92em', marginTop: '1px' }}>{n.times.join(', ')}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // A manual nameplate recolor (esp. an anesthesiologist recolored across their rooms) wins over the
  // shift-derived color so the bullpen brick matches the board.
  const docColor = (e: EmployeeLike) => colorOverrideFor?.(e.employeeId) || doctorBullpenColor(shiftByEmp.get(e.employeeId));
  const crnaColor = (e: EmployeeLike) => colorOverrideFor?.(e.employeeId) || crnaNameplateColor(shiftByEmp.get(e.employeeId));

  // Dropping a staffed plate here removes them from that room (and back into the bullpen if unstaffed).
  const removalDrop = onRemoveStaff ? {
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; },
    onDragEnter: (e: React.DragEvent) => { e.preventDefault(); setDropActive(true); },
    onDragLeave: (e: React.DragEvent) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropActive(false); },
    onDrop: (e: React.DragEvent) => {
      setDropActive(false);
      const raw = e.dataTransfer.getData('application/x-staff');
      if (!raw) return;
      try {
        const { employeeId, fromScheduleId } = JSON.parse(raw);
        if (fromScheduleId && employeeId) onRemoveStaff(fromScheduleId, employeeId);
      } catch { /* ignore */ }
    },
  } : {};

  return (
    <div {...removalDrop} style={{ outline: dropActive ? '2px dashed var(--or-blue-mid)' : 'none', outlineOffset: '4px', borderRadius: '6px' }}>
      <div style={{ fontSize: '0.8em', color: 'var(--text-secondary)', marginBottom: '10px' }}>
        Bullpen — drag a provider onto a room slot, or click a name for details. Staffed providers leave the bullpen. Drag someone here to pull them off a room.
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        {column('Anesthesiologists', doctors.length, <>
          {needNameplate('doctor', doctorNeeds)}
          {shiftDocGroups.map(g => subgroup(g.label, g.items.slice().sort(byLastName), docColor))}
          {subgroup('Pecking order', orderedDocs, docColor, true)}
        </>)}
        {column('CRNAs', crnas.length, <>
          {needNameplate('crna', crnaNeeds)}
          {shiftCrnaGroups.map(g => subgroup(g.label, g.items.slice().sort(byLastName), crnaColor))}
          {subgroup('Pecking order', orderedCrnas, crnaColor, true)}
        </>)}
        {column('Students', students.length, <>
          {subgroup('New', students.filter(e => e.employeeType !== 'nurse-student-senior' && e.studentCompetency === 'new').sort(byLastName), e => studentColorFor(e.employeeType, e.studentCompetency))}
          {subgroup('Intermediate', students.filter(e => e.employeeType !== 'nurse-student-senior' && e.studentCompetency === 'intermediate').sort(byLastName), e => studentColorFor(e.employeeType, e.studentCompetency))}
          {subgroup('Green (senior)', students.filter(e => e.employeeType === 'nurse-student-senior' || e.studentCompetency === 'green').sort(byLastName), e => studentColorFor(e.employeeType, e.studentCompetency))}
          {subgroup('Competency not set', students.filter(e => e.employeeType !== 'nurse-student-senior' && !e.studentCompetency).sort(byLastName), e => studentColorFor(e.employeeType, e.studentCompetency))}
        </>)}
      </div>
    </div>
  );
};

export default Bullpen;
